import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, Circle } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const ksh = (n) =>
  n >= 1e9 ? `KSh ${(n / 1e9).toFixed(2)}bn` : n >= 1e6 ? `KSh ${(n / 1e6).toFixed(1)}m`
    : n ? `KSh ${Math.round(n).toLocaleString()}` : "Not costed yet";

export default function ProjectDetail() {
  const { reference } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/projects/${reference}`).then((r) => setD(r.data)).catch(() => setMissing(true));
  }, [reference]);

  useSeo({
    title: d ? `${d.title} — County Project` : "County Project",
    description: d?.summary || "A county project, condition by condition, with the risks written down.",
  });

  if (missing) {
    return (
      <div className="bg-black py-24" data-testid="project-missing">
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          <h1 className="display-2">That Project Is Not Published</h1>
          <p className="body-copy mt-5">
            It may not have been read yet, or it may have been taken down.{" "}
            <Link to="/projects" className="text-ochre underline">Every published project is here</Link>.
          </p>
        </div>
      </div>
    );
  }
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="project-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="project-detail">
      <div className="mx-auto max-w-[1000px] px-5 md:px-10">
        <div className="flex flex-wrap items-center gap-3">
          <span className="mono border border-ochre/50 px-3 py-1 text-[0.55rem] uppercase tracking-[0.16em] text-ochre"
            data-testid="project-stage">
            {d.stage_label}
          </span>
          <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
            {d.county}{d.ward ? ` · ${d.ward}` : ""} · {d.reference}
          </span>
        </div>
        <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
        <p className="mt-4 text-sm leading-relaxed text-zinc-500">{d.stage_note}</p>
        {d.stage_why ? (
          <p className="mt-4 max-w-3xl border-l-2 border-maasai bg-[#0b0b0b] p-5 text-sm leading-relaxed text-zinc-300"
            data-testid="project-stage-why">
            {d.stage_why}
          </p>
        ) : null}
        {d.summary ? <p className="body-copy mt-6 max-w-3xl">{d.summary}</p> : null}

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-3" data-testid="project-figures">
          {[["What it would cost to build", ksh(d.build_cost_shillings)],
            ["First running year", ksh(d.running_year_shillings)],
            ["Funding route", d.funding_route]].map(([k, v]) => (
            <div key={k} className="bg-[#0b0b0b] p-6">
              <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">{k}</span>
              <span className="mt-3 block font-display text-lg uppercase tracking-tight text-white">{v}</span>
            </div>
          ))}
        </div>

        <section className="mt-16" data-testid="project-conditions">
          <p className="eyebrow">Readiness</p>
          <h2 className="display-3 mt-4">
            {d.readiness.met} Of {d.readiness.total} Conditions Met
          </h2>
          <div className="mt-6 h-1 w-full bg-zinc-900">
            <div className="h-1 bg-ochre" style={{ width: `${d.readiness.pct}%` }} />
          </div>
          <div className="mt-8 grid gap-px bg-zinc-800">
            {d.conditions.map((c) => (
              <div key={c.id} className="bg-[#0b0b0b] p-6" data-testid={`project-condition-${c.id}`}>
                <div className="flex items-start gap-3">
                  {c.met
                    ? <Check size={16} className="mt-[3px] shrink-0 text-ochre" />
                    : <Circle size={14} className="mt-[5px] shrink-0 text-zinc-600" />}
                  <div>
                    <h3 className={`text-sm font-semibold md:text-base ${c.met ? "text-white" : "text-zinc-400"}`}>
                      {c.label}
                      {c.met && c.dated ? (
                        <span className="mono ml-3 text-[0.55rem] uppercase tracking-[0.16em] text-ochre">{c.dated}</span>
                      ) : null}
                    </h3>
                    <p className="mt-2 text-xs leading-relaxed text-zinc-400">{c.note || c.why}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16" data-testid="project-risks">
          <p className="eyebrow">Written down before anyone asks</p>
          <h2 className="display-3 mt-4">Risks And What Could Stop This</h2>
          <div className="mt-8 grid gap-px bg-zinc-800">
            {(d.risks || []).map((r, i) => (
              <div key={i} className="bg-[#0b0b0b] p-6" data-testid={`project-risk-${i}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle size={15} className="mt-[3px] shrink-0 text-maasai" />
                  <div>
                    <p className="text-sm leading-relaxed text-white">{r.risk}</p>
                    {r.mitigation ? (
                      <p className="mt-2 text-xs leading-relaxed text-zinc-400">{r.mitigation}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {(d.contributions || []).length ? (
          <section className="mt-16" data-testid="project-contributions">
            <p className="eyebrow">Given, not paid for</p>
            <h2 className="display-3 mt-4">Contributions In Kind</h2>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">
              Nobody has paid anything into this platform, and nobody has been paid out of it. These are things
              people gave: land on a lease, professional hours, materials, coaching. Credited where they asked to
              be, anonymous where they did not.
            </p>
            <div className="mt-8 border border-zinc-800">
              {d.contributions.map((c, i) => (
                <div key={i} className="grid gap-2 border-b border-zinc-900 p-5 md:grid-cols-[200px_1fr_140px]"
                  data-testid={`project-contribution-${i}`}>
                  <span className="mono text-[0.6rem] uppercase tracking-[0.14em] text-ochre">{c.kind}</span>
                  <span className="text-sm text-zinc-300">{c.detail}</span>
                  <span className="mono text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500 md:text-right">
                    {c.credit}{c.dated ? ` · ${c.dated}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-16" data-testid="project-updates">
          <p className="eyebrow">What moved, and what did not</p>
          <h2 className="display-3 mt-4">The Record</h2>
          {(d.updates || []).length ? (
            <div className="mt-8 border-l-2 border-zinc-800 pl-6">
              {d.updates.map((u, i) => (
                <Reveal key={i} delay={Math.min(i * 0.04, 0.2)}>
                  <div className="pb-8" data-testid={`project-update-${i}`}>
                    <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-ochre">{u.dated}</span>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-300">{u.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="mt-6 text-sm leading-relaxed text-zinc-500" data-testid="project-updates-empty">
              Nothing has been recorded against this project yet. When something moves, or stops moving, it is
              dated and written here.
            </p>
          )}
        </section>

        <section className="mt-16 border-l-2 border-maasai bg-[#0b0b0b] p-7" data-testid="project-regulatory">
          <p className="eyebrow">{d.regulatory.heading}</p>
          <ul className="mt-5 space-y-3">
            {d.regulatory.today.map((t, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                <Check size={14} className="mt-1 shrink-0 text-ochre" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 border-t border-white/10 pt-5 text-xs leading-relaxed text-zinc-400">
            {d.regulatory.later}
          </p>
        </section>

        <div className="mono mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[0.6rem] uppercase tracking-[0.16em]">
          <Link to="/projects" className="text-ochre hover:text-white" data-testid="project-to-index">Every project</Link>
          <Link to="/specimen-heads-of-terms" className="text-ochre hover:text-white" data-testid="project-to-specimen">
            The specimen heads of terms
          </Link>
          <Link to="/apply" className="text-ochre hover:text-white" data-testid="project-to-apply">
            File ground in your county
          </Link>
        </div>
      </div>
    </div>
  );
}
